import { makeStyles } from '@material-ui/core';
import { useSidebarOpenState } from '@backstage/core-components';
import { useShelfFilter } from './SidebarShelf';

/**
 * The heading above a group of navigation entries (DX-302).
 *
 * The target picture groups the sidebar into Main, Discover, Operate, Tools
 * and Starred, and names each group above its entries. What stood here before
 * were `SidebarDivider` rules — which portal-shell.md AC-5 already promised
 * would make the groups visible, and which the DX-275 measurement found they
 * do not: a 1px line in a 224px column reads as texture, not as structure. A
 * reader could see that the list had gaps in it without being able to say what
 * the gaps meant.
 *
 * A word does what the rule could not. "Operate" above Status and Deployments
 * says why those two sit together, so a reader looking for deploy history
 * knows to read that block and skip the others — which is the whole return on
 * grouping a menu.
 *
 * It is a `<h2>` rather than a styled div: the groups are the sidebar's
 * headings, and a reader on a screen reader navigates by them the same way a
 * sighted reader's eye does. The sidebar is already inside a `<nav>` that
 * Backstage labels, so these nest under it.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  /*
    The public portals' `.docs-sidebar-heading` — the Cockpit's `text-mono`
    utility — spaced as their sections are: 0.75rem above a heading, and
    1.35rem more where a group ends and the next begins (DX-352).
  */
  label: {
    padding: '0 1rem 0.25rem',
    margin: '2.1rem 0 0',
    fontSize: '0.6875rem',
    fontWeight: 500,
    lineHeight: 1.15,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    // Muted rather than full-strength: the heading orients, the entries are
    // what a reader acts on, and a heading as loud as its items competes with
    // them. `--ui-nav-label` is the public portals' own sidebar heading, which
    // in dark mode is a quiet grey-violet rather than the secondary text's
    // violet (DX-352).
    color: 'var(--ui-nav-label)',
    // The first group sits directly under the filter.
    '[data-shelf-head] + &': {
      marginTop: '0.75rem',
    },
  },
  /*
    Collapsed to its icons — on a documentation page (DX-352), or on a narrow
    screen — the column has no room for a word, and a clipped one ("DISCOV")
    reads as broken. The word is hidden from sight but kept as the heading a
    screen reader navigates by, and a short rule under the icon column keeps
    the groups apart, which is the one thing a label does that an icon cannot.
  */
  collapsed: {
    position: 'relative',
    height: theme.spacing(2.5),
    margin: 0,
    overflow: 'hidden',
    color: 'transparent',
    fontSize: 1,
    '&::after': {
      content: '""',
      position: 'absolute',
      left: theme.spacing(3),
      width: 24,
      bottom: theme.spacing(0.75),
      height: 1,
      background: 'var(--ui-border)',
    },
    '&:first-child': {
      height: theme.spacing(1.5),
      '&::after': { display: 'none' },
    },
  },
}));

export const SidebarGroupLabel = ({ children }: { children: string }) => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const { filtering } = useShelfFilter();
  // While a query narrows the column, its matches stand as one list, as on
  // the portals: a group name above an entry it has hidden would name nothing.
  if (filtering) {
    return null;
  }
  return (
    <h2 className={isOpen ? classes.label : classes.collapsed}>{children}</h2>
  );
};
